import {useIntl} from "react-intl";
import {PopupElement, PopupProvider} from "../../UIElements";
import React, {useRef} from "react";
import {useSupportBotContext} from "./SupportBotData";
import BotCoordinatorSelector from "./components/BotCoordinatorSelector";
import {Bell, Layers, User} from "lucide-react";
import {useSnackbar} from "../../Content";
import {validateBotVersionName, getDefaultErrorMessage} from "../../utils/botValidation";
import BotAppearance from "./BotAppearance";
import "./BotInformation.scss"

const BotInformation = ({ botData, updateBotData }) => {
    const intl = useIntl();
    const { name, description, status, supportRequestTarget, coordinator, version } = botData;
    const { data: { supportRoles } } = useSupportBotContext()
    const { setSnackbarData } = useSnackbar();
    const originalVersionRef = useRef(version);
    const versions = botData?.versions || []

    const handleVersionBlur = e => {
        const newName = e.target.value.trim();
        const validation = validateBotVersionName(newName, versions, originalVersionRef.current);

        if (!validation.isValid) {
            updateBotData("version", originalVersionRef.current);
            setSnackbarData({
                show: true,
                text: intl.formatMessage({
                    id: validation.errorMessageId,
                    defaultMessage: getDefaultErrorMessage(validation.errorType)
                }),
                state: validation.errorType === 'empty' ? "info" : "error",
                autoHide: true,
            });
            return;
        }

        if (newName !== version) {
            updateBotData("version", newName);
        }
    }

    return (
        <>
            <h4>
                {intl.formatMessage({
                    id: 'survey.navigation.information',
                    defaultMessage: 'Information',
                })}
            </h4>
            <div className={"grid bot-name-container"}>
                <div>
                <label htmlFor={"bot-name"}>
                    {intl.formatMessage({
                        id: 'survey.information.name',
                        defaultMessage: 'Name',
                    })}
                </label>
                <input
                    type={"text"}
                    id={"bot-name"}
                    value={name || ""}
                    onChange={e => updateBotData("name", e.target.value)}
                    placeholder={intl.formatMessage({
                        id: 'botName.placeholder',
                        defaultMessage: 'Give a name for your bot',
                    })}
                />
            </div>
            <div>
                <label htmlFor={"bot-version"}>
                    <Layers size={16} />
                    {intl.formatMessage({
                        id: 'version',
                        defaultMessage: 'Version',
                    })}
                </label>
                {version === "DEFAULT" ?
                    <input type={"text"}
                           disabled
                           value={intl.formatMessage({
                               id: 'default',
                               defaultMessage: 'Default',
                           })}
                    /> :
                    <input
                        type={"text"}
                        id={"bot-version"}
                        value={version || ""}
                        onChange={e => updateBotData("version", e.target.value)}
                        onFocus={() => originalVersionRef.current = version}
                        onBlur={handleVersionBlur}
                        placeholder={intl.formatMessage({
                            id: 'botVersion.placeholder',
                            defaultMessage: 'Bot version name',
                        })}
                    />
                }
            </div>
            </div>
            <BotAppearance
                name={name}
                icon={botData.icon}
                color={botData.color}
                updateBotData={updateBotData}
            />
            <div>
                <label htmlFor={"bot-description"}>
                    {intl.formatMessage({
                        id: 'description',
                        defaultMessage: 'Description',
                    })}
                </label>
                <textarea
                    id={"bot-description"}
                    value={description || ""}
                    onChange={e => updateBotData("description", e.target.value)}
                    placeholder={intl.formatMessage({
                        id: 'botDescription.placeholder',
                        defaultMessage: 'Write a short description about the bot, and how it supports students.',
                    })}
                    rows={3}
                />
            </div>
            <div className={"survey-dropdown"}>
                <label htmlFor={"bot-role"}>
                    <Bell />
                    {intl.formatMessage({
                        id:'supportRequestTarget',
                        defaultMessage:'Target for Support Requests'
                    })}
                </label>
                <select
                    id={"bot-role"}
                    value={supportRequestTarget || ""}
                    onChange={e => updateBotData("supportRequestTarget", e.target.value)}
                    >
                    <option value={""}>
                        {intl.formatMessage({
                            defaultMessage:"Support Requests Disabled",
                            id:'supportRequestsDisabled',
                        })}
                    </option>
                    {Object.keys(supportRoles[0]).map((role,i) => {
                        return <option key={i} value={role}>
                            {intl.formatMessage({
                                defaultMessage: role,
                                id: `role.${role}`,
                            })}
                        </option>
                    })}
                </select>
            </div>
            <div className={"flex column"}>
                <label htmlFor={"bot-coordinator"}>
                    <User />
                    {intl.formatMessage({
                        id:'botCoordinator',
                        defaultMessage:'Bottivastaava'
                    })}
                </label>
                <BotCoordinatorSelector
                    id={"bot-coordinator"}
                    value={coordinator || ""}
                    onChange={value => updateBotData("coordinator", value)}
                />
            </div>
        </>
    );
};
export default BotInformation